export type LegacyDealPartyRole = "buyer" | "cobuyer" | "cosigner" | "dealer"
export type DealPartyRole = "buyer" | "dealer"
export type BuyerType = "primary" | "co_buyer" | "co_signer"
export type BuyerLikeRole = Exclude<LegacyDealPartyRole, "dealer">
export const ALL_BUYERS_SIGNER_ASSIGNMENT = "all_buyers"
export const MAX_BUYER_SIGNER_SLOTS = 10
export const BUYER_SIGNER_SLOTS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const
export const BUYER_SIGNER_ROLE_KEYS = [
  "buyer:1",
  "buyer:2",
  "buyer:3",
  "buyer:4",
  "buyer:5",
  "buyer:6",
  "buyer:7",
  "buyer:8",
  "buyer:9",
  "buyer:10",
] as const
export type BuyerSignerSlot = (typeof BUYER_SIGNER_SLOTS)[number]
export type BuyerSignerRoleKey = (typeof BUYER_SIGNER_ROLE_KEYS)[number]

export function isBuyerLikeRole(role: string): role is BuyerLikeRole {
  return role === "buyer" || role === "cobuyer" || role === "cosigner"
}

export function getBuyerTypeFromLegacyRole(role: string): BuyerType | undefined {
  switch (role) {
    case "buyer":
      return "primary"
    case "cobuyer":
      return "co_buyer"
    case "cosigner":
      return "co_signer"
    default:
      return undefined
  }
}

export function getLegacyRoleFromBuyerType(buyerType: BuyerType): BuyerLikeRole {
  switch (buyerType) {
    case "primary":
      return "buyer"
    case "co_buyer":
      return "cobuyer"
    case "co_signer":
      return "cosigner"
  }
}

export function getDefaultBuyerSignerSlot(buyerType: BuyerType): BuyerSignerSlot {
  switch (buyerType) {
    case "primary":
      return 1
    case "co_buyer":
      return 2
    case "co_signer":
      return 3
  }
}

export function getDefaultBuyerSignerRoleKey(buyerType: BuyerType): BuyerSignerRoleKey {
  return getBuyerSignerRoleForSlot(getDefaultBuyerSignerSlot(buyerType)) as BuyerSignerRoleKey
}

export function getNormalizedBuyerType(
  role: string,
  buyerType?: string | null,
): BuyerType | undefined {
  if (buyerType === "primary" || buyerType === "co_buyer" || buyerType === "co_signer") {
    return buyerType
  }

  return getBuyerTypeFromLegacyRole(role)
}

export function compareBuyerTypes(left: BuyerType, right: BuyerType) {
  return getBuyerTypeSortOrder(left) - getBuyerTypeSortOrder(right)
}

function getBuyerTypeSortOrder(buyerType: BuyerType) {
  switch (buyerType) {
    case "primary":
      return 0
    case "co_buyer":
      return 1
    case "co_signer":
      return 2
  }
}

export function getBuyerSignerRole(index: number): string {
  return BUYER_SIGNER_ROLE_KEYS[index] ?? `buyer:${index + 1}`
}

export function getBuyerSignerRoleForSlot(slot: number): string {
  return getBuyerSignerRole(slot - 1)
}

export function getBuyerSignerSlot(value: string | null | undefined) {
  if (!value?.startsWith("buyer:")) {
    return undefined
  }

  const parsed = Number.parseInt(value.slice("buyer:".length), 10)
  if (!Number.isInteger(parsed) || parsed < 1 || parsed > MAX_BUYER_SIGNER_SLOTS) {
    return undefined
  }

  return parsed as BuyerSignerSlot
}

export function getBuyerSignerIndex(value: string | null | undefined) {
  const slot = getBuyerSignerSlot(value)
  return slot === undefined ? undefined : slot - 1
}

export function isIndexedBuyerSignerRole(value: string | null | undefined) {
  return getBuyerSignerIndex(value) !== undefined
}

export function formatSignerAssignmentLabel(value: string | null | undefined) {
  const buyerIndex = getBuyerSignerIndex(value)
  if (buyerIndex !== undefined) {
    return `Buyer ${buyerIndex + 1}`
  }

  switch (value) {
    case ALL_BUYERS_SIGNER_ASSIGNMENT:
      return "All buyers"
    case "buyer":
      return "Buyer"
    case "cobuyer":
      return "Co-buyer"
    case "cosigner":
      return "Co-signer"
    case "dealer":
      return "Dealer"
    default:
      return "Unassigned"
  }
}

type BuyerSignerParty = {
  _id?: string | number | { toString(): string }
  role: string
  buyerType?: string | null
  signerSlot?: number | null
  createdAt?: number
}

export function sortBuyerSignerParties<T extends BuyerSignerParty>(parties: T[]) {
  return [...parties].sort((left, right) => {
    if (left.signerSlot != null || right.signerSlot != null) {
      if (left.signerSlot == null) return 1
      if (right.signerSlot == null) return -1

      const signerOrder = left.signerSlot - right.signerSlot
      if (signerOrder !== 0) return signerOrder
    }

    const leftBuyerType = getNormalizedBuyerType(left.role, left.buyerType)
    const rightBuyerType = getNormalizedBuyerType(right.role, right.buyerType)

    if (leftBuyerType && rightBuyerType) {
      const buyerTypeOrder = compareBuyerTypes(leftBuyerType, rightBuyerType)
      if (buyerTypeOrder !== 0) {
        return buyerTypeOrder
      }
    }

    const createdAtOrder = (left.createdAt ?? 0) - (right.createdAt ?? 0)
    if (createdAtOrder !== 0) {
      return createdAtOrder
    }

    return String(left._id ?? "").localeCompare(String(right._id ?? ""))
  })
}

export function getSignerRoleForParty<T extends BuyerSignerParty>(party: T, parties: T[]) {
  if (party.role === "dealer") {
    return "dealer"
  }

  if (party.signerSlot != null && party.signerSlot >= 1) {
    return getBuyerSignerRoleForSlot(party.signerSlot)
  }

  if (!isBuyerLikeRole(party.role)) {
    return party.role
  }

  const buyerLikeParties = sortBuyerSignerParties(
    parties.filter((item) => isBuyerLikeRole(item.role)),
  )
  const index = buyerLikeParties.findIndex(
    (item) => String(item._id ?? "") === String(party._id ?? ""),
  )

  return index >= 0 ? getBuyerSignerRole(index) : party.role
}

export function matchesSignerAssignment(
  fieldSignerRole: string | null | undefined,
  recipientRole: string,
  recipientSignerRole?: string | null,
) {
  if (!fieldSignerRole) {
    return true
  }

  if (fieldSignerRole === ALL_BUYERS_SIGNER_ASSIGNMENT) {
    return isBuyerLikeRole(recipientRole) || isIndexedBuyerSignerRole(recipientSignerRole)
  }

  const buyerIndex = getBuyerSignerIndex(fieldSignerRole)
  if (buyerIndex !== undefined) {
    return recipientSignerRole === fieldSignerRole
  }

  return fieldSignerRole === recipientRole
}
